import { Granularity, PeriodPreset } from "../../../types/unit-economics.types";

export interface PeriodRange {
  from: string; // YYYY-MM-DD (inclusive)
  to: string; // YYYY-MM-DD (exclusive, half-open per spec D3)
}

const pad = (n: number): string => String(n).padStart(2, "0");

const toDateString = (date: Date): string =>
  `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;

/**
 * Compute the half-open range [from, to) for a rolling window of `days` ending
 * today (inclusive). The exclusive upper bound is the start of tomorrow, so the
 * current day's data is always included — this is what makes "Ostatnie 28 dni"
 * show fresh signups that the calendar presets (previous complete period) miss.
 */
const rollingRange = (days: number): PeriodRange => {
  const now = new Date();
  const end = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1);
  const start = new Date(
    end.getFullYear(),
    end.getMonth(),
    end.getDate() - days,
  );
  return { from: toDateString(start), to: toDateString(end) };
};

/**
 * Compute the half-open range [from, to) for a preset.
 * - Rolling presets (28/90/365 days) end today and include the current day.
 * - Calendar presets (month/quarter/year) look at the immediately preceding
 *   COMPLETE period; `to` is exclusive (the first day of the current period).
 */
export const computePresetRange = (preset: PeriodPreset): PeriodRange => {
  const now = new Date();
  const year = now.getFullYear();
  const month = now.getMonth(); // 0-11

  switch (preset) {
    case "last-28-days":
      return rollingRange(28);
    case "last-90-days":
      return rollingRange(90);
    case "last-365-days":
      return rollingRange(365);
    case "last-month": {
      const start = new Date(year, month - 1, 1);
      const end = new Date(year, month, 1);
      return { from: toDateString(start), to: toDateString(end) };
    }
    case "last-quarter": {
      const currentQuarter = Math.floor(month / 3); // 0-3
      const startMonth = (currentQuarter - 1) * 3;
      const start = new Date(year, startMonth, 1);
      const end = new Date(year, currentQuarter * 3, 1);
      return { from: toDateString(start), to: toDateString(end) };
    }
    case "last-year": {
      const start = new Date(year - 1, 0, 1);
      const end = new Date(year, 0, 1);
      return { from: toDateString(start), to: toDateString(end) };
    }
    case "custom":
    default: {
      // Default custom seed = last month range.
      const start = new Date(year, month - 1, 1);
      const end = new Date(year, month, 1);
      return { from: toDateString(start), to: toDateString(end) };
    }
  }
};

/**
 * Compute the equal-length period directly preceding [from, to).
 * Used to label the "previous period" comparison with concrete dates.
 * Returns null when either bound is missing or the range is non-positive.
 */
export const computePreviousRange = (
  range: PeriodRange,
): PeriodRange | null => {
  if (!range.from || !range.to) return null;
  const from = new Date(range.from);
  const to = new Date(range.to);
  const lengthMs = to.getTime() - from.getTime();
  if (lengthMs <= 0) return null;
  const prevTo = new Date(from.getTime());
  const prevFrom = new Date(from.getTime() - lengthMs);
  return { from: toDateString(prevFrom), to: toDateString(prevTo) };
};

/**
 * Format a half-open [from, to) range as an inclusive, human-readable label.
 * `to` is exclusive, so the displayed end date is the day before `to`.
 */
export const formatRangeLabel = (range: PeriodRange): string => {
  // Parse both boundaries as LOCAL dates. `new Date('YYYY-MM-DD')` parses as UTC
  // midnight; rendered in Poland (UTC+1/+2) the displayed day can shift by one.
  const [fy, fm, fd] = range.from.split("-").map(Number);
  const [ty, tm, td] = range.to.split("-").map(Number);
  const start = new Date(fy, fm - 1, fd); // local midnight
  const inclusiveEnd = new Date(ty, tm - 1, td - 1); // local midnight, minus 1 day
  const fmt = (d: Date): string => d.toLocaleDateString("pl-PL");
  return `${fmt(start)} – ${fmt(inclusiveEnd)}`;
};

/**
 * Map a preset to its natural granularity (used to set the granularity
 * selector when a preset is chosen).
 */
export const presetGranularity = (preset: PeriodPreset): Granularity => {
  switch (preset) {
    case "last-quarter":
    case "last-90-days":
      return "quarter";
    case "last-year":
    case "last-365-days":
      return "year";
    case "last-month":
    case "last-28-days":
    case "custom":
    default:
      return "month";
  }
};
